import React, { createContext, useContext, useMemo, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useCollection, useDocumentData } from "react-firebase-hooks/firestore";
import { doc, query, where, collection, orderBy, limit } from "firebase/firestore";

import { auth, db } from "../utils/firebase";

const eventsRef = collection(db, "events");
const partyUsersRef = collection(db, "partyUsers");
const userScoresRef = collection(db, "userScores");
const userPlacementsRef = collection(db, "userPlacements");
const chatMessagesRef = collection(db, "chatMessages");

interface AppState {
  isReady: boolean;
  user?: User | null;
  event?: ESCEvent;
  myPartyUsers: PartyUser[];
  selectedPartyId: string | null;
  partyUser?: PartyUser;
  userScore?: UserScore;
  party?: Party;
  partyScores?: PartyScores;
  partyUsers?: PartyUser[];
  globalUser?: AppUser;
  placements?: UserPlacement;
  blurBackground: boolean;
  latestChatMessage?: ChatMessage;
  chatUnread: boolean;
}

interface AppContextValue {
  state: AppState;
  setBlurBackground: (blur: boolean) => void;
  setSelectedPartyId: (partyId: string | null) => void;
}

const initialState: AppState = {
  isReady: false,
  myPartyUsers: [],
  selectedPartyId: null,
  blurBackground: false,
  chatUnread: false,
};

const AppContext = createContext<AppContextValue>({
  state: initialState,
  setBlurBackground: () => {},
  setSelectedPartyId: () => {},
});

function AppProvider({ children }: { children: React.ReactElement }) {
  const [blurBackground, setBlurBackground] = useState(false);
  const [selectedPartyId, setSelectedPartyId] = useState<string | null>(null);
  const [user, userLoading] = useAuthState(auth, {
    // onUserChanged: async (user) => {
    //   console.log("user changed?");
    // },
  });

  // console.log({ user });

  const [globalUser] = useDocumentData(doc(db, "users", user?.uid ?? "foobar"));

  // Listen for an active event
  const [eventValue, eventLoading] = useCollection(
    query(eventsRef, where("active", "==", true))
  );

  const event =
    !eventLoading && eventValue?.docs?.length === 1
      ? ({
          ...eventValue.docs[0].data(),
          _docId: eventValue.docs[0].id,
        } as ESCEvent)
      : undefined;

  // console.log({ eventValue }, { eventLoading }, { event });

  // For overriding while developing
  // const eventLoading = false;
  // const event = {
  //   active: true,
  //   // stage: "pre-contest",
  //   // stage: "score-songs",
  //   // stage: "calculate-placement",
  //   // stage: "guess-placement",
  //   // stage: "calculate-results",
  //   // stage: "show-results",
  //   // stage: "after-contest",

  //   title: "Grand-local-2",
  //   // _docId: "KCDZFg5yaVprgFH9tyro", // Semi final 1
  //   // _docId: "T5HrGZ16NOBxfeQ3JYib", // Semi final 2
  //   _docId: "qsuTJ9ynxpqwQdKiGrpe", // Grand Final
  // };

  // Get an active local user for the current event / party
  const [myPartyUsersValue, myPartyUsersLoading] = useCollection(
    query(
      partyUsersRef,
      where("active", "==", true),
      where("userId", "==", user?.uid ?? "foobar"),
      where("eventId", "==", event?._docId ?? "foobar")
    )
  );

  const myPartyUsers: PartyUser[] = useMemo(() => {
    if (myPartyUsersLoading || !myPartyUsersValue) return [];
    return myPartyUsersValue.docs.map(
      (d) => ({ ...d.data(), _docId: d.id } as PartyUser)
    );
  }, [myPartyUsersLoading, myPartyUsersValue]);

  // Auto-clear selection if the selected party is no longer in my memberships
  // (e.g. user left it). Done via render-time check rather than effect so the
  // rest of the tree sees a consistent state.
  const effectiveSelectedPartyId =
    selectedPartyId &&
    myPartyUsers.some((pu) => pu.partyId === selectedPartyId)
      ? selectedPartyId
      : null;

  const partyUser = effectiveSelectedPartyId
    ? myPartyUsers.find((pu) => pu.partyId === effectiveSelectedPartyId)
    : undefined;

  // User-scoped scores for the active event
  const userScoreDocId =
    user?.uid && event?._docId ? `${user.uid}_${event._docId}` : "foobar";
  const [userScore] = useDocumentData(doc(db, "userScores", userScoreDocId));

  // The Party doc for the selected party
  const [party] = useDocumentData(
    doc(db, "parties", effectiveSelectedPartyId ?? "foobar")
  );

  // All party members in the selected party
  const [partyUsersValue, partyUsersLoading] = useCollection(
    query(
      partyUsersRef,
      where("partyId", "==", effectiveSelectedPartyId ?? "foobar"),
      where("active", "==", true)
    )
  );

  const memberPartyUsers: PartyUser[] = useMemo(() => {
    if (partyUsersLoading || !partyUsersValue) return [];
    return partyUsersValue.docs.map(
      (d) => ({ ...d.data(), _docId: d.id } as PartyUser)
    );
  }, [partyUsersLoading, partyUsersValue]);

  const memberUserIds = useMemo(
    () => memberPartyUsers.map((pu) => pu.userId).slice(0, 30),
    [memberPartyUsers]
  );

  // All userScores for the members in the selected party (for the current event)
  const [memberScoresValue, memberScoresLoading] = useCollection(
    memberUserIds.length > 0 && event?._docId
      ? query(
          userScoresRef,
          where("eventId", "==", event._docId),
          where("userId", "in", memberUserIds)
        )
      : null
  );

  const partyScores: PartyScores | undefined = useMemo(() => {
    if (!effectiveSelectedPartyId) return undefined;
    if (memberScoresLoading) return undefined;
    const result: PartyScores = {};
    if (!memberScoresValue) return result;
    memberScoresValue.docs.forEach((d) => {
      const data = d.data() as UserScore;
      const userId = data.userId;
      if (!data.data) return;
      Object.keys(data.data).forEach((songId) => {
        if (!result[songId]) result[songId] = {};
        // @ts-ignore PartyScores values are keyed by userId
        result[songId][userId] = data.data[songId];
      });
    });
    return result;
  }, [effectiveSelectedPartyId, memberScoresLoading, memberScoresValue]);

  // All userPlacements for party members (for the current event) — used to
  // attach finalScore to each PartyUser for the leaderboard.
  const [memberPlacementsValue] = useCollection(
    memberUserIds.length > 0 && event?._docId
      ? query(
          userPlacementsRef,
          where("eventId", "==", event._docId),
          where("userId", "in", memberUserIds)
        )
      : null
  );

  const finalScoresByUserId = useMemo(() => {
    const map: Record<string, number> = {};
    memberPlacementsValue?.docs.forEach((d) => {
      const data = d.data() as UserPlacement;
      if (typeof data.finalScore === "number") {
        map[data.userId] = data.finalScore;
      }
    });
    return map;
  }, [memberPlacementsValue]);

  const partyUsers: PartyUser[] = useMemo(
    () =>
      memberPartyUsers.map((pu) => ({
        ...pu,
        finalScore: finalScoresByUserId[pu.userId] ?? 0,
      })),
    [memberPartyUsers, finalScoresByUserId]
  );

  // The current user's own placement guesses for this event
  const [placements] = useDocumentData(
    doc(db, "userPlacements", userScoreDocId)
  );

  // Latest chat message in the selected party (for the unread indicator).
  // Uses the (partyId, created) composite index.
  const [latestChatValue] = useCollection(
    effectiveSelectedPartyId
      ? query(
          chatMessagesRef,
          where("partyId", "==", effectiveSelectedPartyId),
          orderBy("created", "desc"),
          limit(1)
        )
      : null
  );

  const latestChatMessage: ChatMessage | undefined = useMemo(() => {
    const docSnap = latestChatValue?.docs?.[0];
    if (!docSnap) return undefined;
    return { ...docSnap.data(), _docId: docSnap.id } as ChatMessage;
  }, [latestChatValue]);

  const chatUnread = useMemo(() => {
    if (!latestChatMessage || !user) return false;
    if (latestChatMessage.userId === user.uid) return false;
    const latestTime = (latestChatMessage.created as any)?.toMillis?.() ?? 0;
    if (!latestTime) return false;
    const seenTime =
      (partyUser?.lastSeenMessageAt as any)?.toMillis?.() ?? 0;
    return latestTime > seenTime;
  }, [latestChatMessage, partyUser, user]);

  return (
    <AppContext.Provider
      value={{
        state: {
          isReady: !userLoading,
          user,
          event,
          myPartyUsers,
          selectedPartyId: effectiveSelectedPartyId,
          partyUser,
          userScore: userScore as UserScore,
          party: party as Party,
          partyScores,
          partyUsers,
          globalUser: globalUser as AppUser,
          placements: placements as UserPlacement,
          blurBackground,
          latestChatMessage,
          chatUnread,
        },
        setBlurBackground,
        setSelectedPartyId,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

function useApp() {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error("useApp must be used within the AppProvider");
  }

  return context;
}

export { AppProvider, useApp };
